/**
 * Thin fetch wrapper around the public `subscribe.*` tRPC mutations.
 *
 * We avoid `@trpc/client` here for the same reason `NewsFeed` does: this
 * surface only calls four procedures, all of them on a public endpoint
 * with no auth header rewriting. A hand-rolled fetch keeps the bundle
 * small and the network shape obvious.
 *
 * Wire format (tRPC v11 + superjson transformer):
 *   POST /api/trpc/subscribe.create
 *   body: { json: <input> }
 *   response: { result: { data: { json: <output> } } }
 *
 * `429` responses are surfaced as `RateLimitError` so the caller can
 * show a localised "try again later" toast instead of a generic error.
 */

import type {
  SubscribeConfirmOutput,
  SubscribeCreateOutput,
  SubscribeFormValues,
  SubscribePreferencesOutput,
  SubscribeUnsubscribeOutput,
  SubscriberDigestFrequency,
  SubscriberLocale,
} from "@hantavirus/shared";

export class RateLimitError extends Error {
  constructor(message = "rate-limited") {
    super(message);
    this.name = "RateLimitError";
  }
}

export class NetworkError extends Error {
  status: number;
  constructor(status: number, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.name = "NetworkError";
    this.status = status;
  }
}

function resolveBaseUrl(override?: string): string {
  const fallback =
    typeof import.meta !== "undefined" && typeof import.meta.env !== "undefined"
      ? (import.meta.env.VITE_API_URL as string | undefined)
      : undefined;
  return (override ?? fallback ?? "").replace(/\/+$/u, "");
}

async function trpcMutation<TOutput>(
  procedure: string,
  input: unknown,
  opts: { locale?: string; apiBaseUrl?: string } = {},
): Promise<TOutput> {
  const base = resolveBaseUrl(opts.apiBaseUrl);
  const url = `${base}/api/trpc/${procedure}`;
  const headers: Record<string, string> = {
    "content-type": "application/json",
    accept: "application/json",
  };
  if (opts.locale) headers["x-locale"] = opts.locale;

  const res = await fetch(url, {
    method: "POST",
    headers,
    credentials: "omit",
    body: JSON.stringify({ json: input }),
  });

  if (res.status === 429) {
    throw new RateLimitError();
  }
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as {
        error?: { message?: string; data?: { code?: string } };
      };
      if (body?.error?.data?.code === "TOO_MANY_REQUESTS") {
        throw new RateLimitError(body.error.message);
      }
      if (body?.error?.message) message = body.error.message;
    } catch (parseErr) {
      if (parseErr instanceof RateLimitError) throw parseErr;
      // fall through with generic message
    }
    throw new NetworkError(res.status, message);
  }

  const json = (await res.json()) as {
    result?: {
      data?: { json?: TOutput } | TOutput;
    };
  };
  const inner = json.result?.data;
  const data =
    inner && typeof inner === "object" && "json" in (inner as object)
      ? (inner as { json: TOutput }).json
      : (inner as TOutput | undefined);
  if (data === undefined) {
    throw new NetworkError(500, "malformed response");
  }
  return data;
}

export async function subscribeCreate(
  values: SubscribeFormValues,
  opts: { apiBaseUrl?: string } = {},
): Promise<SubscribeCreateOutput> {
  return trpcMutation<SubscribeCreateOutput>("subscribe.create", values, {
    locale: values.locale,
    apiBaseUrl: opts.apiBaseUrl,
  });
}

export async function subscribeConfirm(
  token: string,
  opts: { locale?: string; apiBaseUrl?: string } = {},
): Promise<SubscribeConfirmOutput> {
  return trpcMutation<SubscribeConfirmOutput>(
    "subscribe.confirm",
    { token },
    opts,
  );
}

export async function subscribeUnsubscribe(
  token: string,
  opts: { locale?: string; apiBaseUrl?: string } = {},
): Promise<SubscribeUnsubscribeOutput> {
  return trpcMutation<SubscribeUnsubscribeOutput>(
    "subscribe.unsubscribe",
    { token },
    opts,
  );
}

export type SubscribePreferencesPayload = {
  token: string;
  locale?: SubscriberLocale;
  digestFrequency?: SubscriberDigestFrequency;
  pushEnabled?: boolean;
};

export async function subscribePreferences(
  payload: SubscribePreferencesPayload,
  opts: { locale?: string; apiBaseUrl?: string } = {},
): Promise<SubscribePreferencesOutput> {
  return trpcMutation<SubscribePreferencesOutput>(
    "subscribe.preferences",
    payload,
    opts,
  );
}

export type SubscribeEnablePushPayload = {
  token: string;
  endpoint: string;
  p256dh: string;
  auth: string;
  userAgent?: string;
};

export type SubscribeEnablePushOutput = {
  status: "enabled" | "invalid-or-expired";
};

export type SubscribeDisablePushPayload = {
  token: string;
  endpoint: string;
};

export type SubscribeDisablePushOutput = {
  status: "disabled" | "invalid-or-expired";
};

async function enablePush(
  payload: SubscribeEnablePushPayload & { apiBaseUrl?: string },
): Promise<SubscribeEnablePushOutput> {
  const { apiBaseUrl, ...input } = payload;
  return trpcMutation<SubscribeEnablePushOutput>(
    "subscribe.enablePush",
    input,
    { apiBaseUrl },
  );
}

async function disablePush(
  payload: SubscribeDisablePushPayload & { apiBaseUrl?: string },
): Promise<SubscribeDisablePushOutput> {
  const { apiBaseUrl, ...input } = payload;
  return trpcMutation<SubscribeDisablePushOutput>(
    "subscribe.disablePush",
    input,
    { apiBaseUrl },
  );
}

/** Convenience namespace for the PWA / push helpers. */
export const subscribeApi = {
  create: subscribeCreate,
  confirm: subscribeConfirm,
  unsubscribe: subscribeUnsubscribe,
  preferences: subscribePreferences,
  enablePush,
  disablePush,
};
